import { Button } from '@components/button';
import { triggerConfetti } from '@components/confetti/provider';
import { IconPlay } from '@components/icons/play';
import { LabelTextGroup } from '@components/label-text-group';
import { Spinner } from '@components/loading-spinner';
import { hideSidebar, Sidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { useScopedTranslate } from '@locale/translate';

import { useIsOutOfCredits } from 'api/authentication/hooks';
import {
  useJourneyTravellerAction,
  useRunJourneyTravellerAction,
} from 'api/traveller-actions/hooks';
import { getActionStatus } from 'ui/authenticated/main/helpers';
import { actionErrorTitle } from 'ui/traveller-journey-builder/node-types/common';

import { ActionProgressBar, isActionError, SidebarProps, TravellerActionActions } from '../common';

type ActionType = 'journey_start';

export const SidebarContent = ({ shortId }: SidebarProps) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const travellerActionQuery = useJourneyTravellerAction<ActionType>(shortId);
  const isOutOfCredits = useIsOutOfCredits();
  // @ts-ignore-next-line travellerActionQuery.data will not be undefined when the mutation is called
  const runMutation = useRunJourneyTravellerAction(travellerActionQuery.data, {
    onSuccess: () => {
      hideSidebar();
      triggerConfetti();
    },
  });

  if (travellerActionQuery.isLoading) {
    return <Spinner />;
  }
  if (!shortId || !travellerActionQuery.data) {
    return <div>{t('dataNotFound')}</div>;
  }

  const action = travellerActionQuery.data;

  const isError = isActionError(action);
  const status = getActionStatus(action);

  const progressText =
    status === 'completed'
      ? t('journeyStarted')
      : isError
      ? t('error')
      : status === 'in_progress'
      ? t('inProgress')
      : t('journeyNotStarted');

  return (
    <Sidebar>
      <Sidebar.SimpleHeader title={t('startJourney')} icon={IconPlay} iconColor="green100">
        <TravellerActionActions action={action} />
      </Sidebar.SimpleHeader>
      <Sidebar.Content>
        <Stack space={4}>
          <LabelTextGroup label={t('description')} text={t('startDescription')} />
          <ActionProgressBar
            action={action}
            progressText={progressText}
            isError={isError}
            errorMessage={
              isError
                ? {
                    title: actionErrorTitle[action.action],
                    description: action.result?.message,
                    date: action.result?.dateFailed,
                  }
                : undefined
            }
            dateSuccess={action.result?.dateSucceeded}
          />
        </Stack>
      </Sidebar.Content>
      <Sidebar.Footer>
        {action.status !== 'completed' && (
          <Stack space={4} style={{ marginTop: 'auto' }}>
            <Stack orientation="horizontal" justify="flex-end">
              <Button
                variant="primary-blue"
                iconLeft={<IconPlay />}
                text={t('startJourney')}
                isDisabled={isOutOfCredits}
                onClick={() => runMutation.mutate({})}
              />
            </Stack>
          </Stack>
        )}
      </Sidebar.Footer>
    </Sidebar>
  );
};

const sidebarLocale = {
  en: {
    startJourney: 'Start journey',
    dataNotFound: 'Data not found',
    journeyStarted: 'Journey started',
    completed: 'Completed',
    description: 'Description',
    startDescription: 'Start event for the journey',
    automatic: 'Automatic',
    manual: 'Manual',
    unconfigured: 'This journey cannot be started. {0} automatic action/s are still unconfigured.',
    error: 'Error',
    inProgress: 'In progress',
    journeyNotStarted: 'Journey not started',
  },
};
